<script setup lang="ts">
import { computed, ref, toRef, unref } from 'vue';
import { useDialogPluginComponent } from 'quasar';
import { useForm } from 'vee-validate';
import * as yup from 'yup';

import type { ChoiceDto, Locales } from '@/services/types/api.types';
import type { SavingOptions } from '@/services/types/file-uploader.types';
import { useLanguageValidationErrors } from '@/composables';

import LanguageSwitcher from '@/components/LanguageSwitcher.vue';
import LocalizedInput from '@/components/LocalizedInput.vue';
import { useI18n } from 'vue-i18n';
import { wrapSavingOperation } from '@/helpers/notifications.helper';
import { useFormsStore } from '@/stores';
import ImageField from '@/components/ImageField.vue';
import { localizableString } from '@/helpers/validation.helper';
import { provideEditLocale } from '@/composables/data-display-utils';

const { t } = useI18n();
const formsStore = useFormsStore();

// PROPS
const props = defineProps<{
  mode: 'add' | 'edit';
  choice: ChoiceDto;
  stepId: number;
  formId: number;
  languageEnabled: Locales[];
}>();

// DIALOG SET-UP
defineEmits([...useDialogPluginComponent.emits]);
const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();

// FORM
const { handleSubmit, errors, meta, setFieldValue } = useForm({
  initialValues: props.choice,
  validationSchema: yup.object<ChoiceDto>({
    text: localizableString(props.languageEnabled),
  }),
});
const languageErrors = useLanguageValidationErrors(errors);
const editLocale = provideEditLocale(toRef(() => props.languageEnabled));
const imageRef = ref(props.choice.image || { url: null });
const imageDataRef = ref<string | null>(null);

const isDirty = computed(() => {
  return unref(meta).dirty;
});

const dialogTitle = computed(() =>
  props.mode === 'add' ? t('form.dialogs.matrix-choice.create') : t('form.dialogs.matrix-choice.edit'),
);

const onSubmit = handleSubmit(async (values) => {
  console.debug('submitting', values);
  await wrapSavingOperation(
    async () =>
      await formsStore.saveChoice(values, props.formId, props.stepId, {
        image: imageDataRef.value,
        version: 'v1',
      }),
    () => onDialogOK(),
  );
});
const handleClearImage = async () => {
  imageRef.value = { url: null };
  imageDataRef.value = null;
  setFieldValue('image', null);
};

const handleSaveImage = async (options: SavingOptions) => {
  imageRef.value.url = options.image!;
  imageDataRef.value = options.image!;
};
</script>

<template>
  <q-dialog ref="dialogRef" @hide="onDialogHide" persistent>
    <q-card class="q-dialog-plugin" v-if="props.choice" style="width: 600px">
      <q-card-section class="row">
        <div class="text-h6">{{ dialogTitle }}</div>
        <q-space />
        <language-switcher
          v-model="editLocale"
          :has-validation-errors="languageErrors"
          :language-enabled="languageEnabled"
        />
      </q-card-section>
      <q-separator />
      <q-card-section>
        <localized-input
          name="text"
          label="Texte"
          :editLocale="editLocale"
          outlined
          :language_enabled="languageEnabled"
        />
        <image-field
          :label="t('form.question.image.label')"
          :source="imageRef.url"
          :target-width="500"
          :target-height="500"
          @clear-image="handleClearImage"
          @save-image="handleSaveImage"
          :form-dirty="isDirty"
          :language-enabled="languageEnabled"
        />
      </q-card-section>
      <q-separator />
      <q-card-actions align="right">
        <q-btn color="primary" flat :label="t('global.cancel')" @click="onDialogCancel" />
        <q-btn color="primary" :label="t('global.save')" @click="onSubmit" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
